import { useMutation, useQuery } from '@tanstack/react-query'

import type { NlpSearchFlightsResponse, StructuredMultiSearchRequest } from '../types/chat.js'
import type { ExtractedBookingData } from '../types/extracted-booking.js'
import type {
  PriceOfferRequest,
  PriceOfferResponse,
  SearchFlightsRequest,
  SearchFlightsResponse,
} from '../types/research-bar.js'

import { apiClient } from '../lib/client.js'

const BOOKING_SESSION_STALE_TIME = 20 * 60 * 1000 // 20 minutes
const SEARCH_FLIGHTS_TIMEOUT_MS = 2 * 60 * 1000

/**
 * Racines des caches de recherche et de pricing. Elles vivent 20 min côté client,
 * autant que le cache d'offres Affidium : quand ce dernier expire, tout ce qui porte
 * l'ancien `transactionKey` est mort et doit être purgé d'un bloc avant de relancer
 * une recherche (cf. `useRestartFlightSearch` côté web).
 */
export const flightSearchQueryKeys = {
  multiSearch: ['searchMultiFlights'] as const,
  priceOffer: ['price-offer'] as const,
  search: ['searchFlights'] as const,
}

const searchFlights = async (request: SearchFlightsRequest, signal: AbortSignal) => {
  const response = await apiClient.client.post<SearchFlightsResponse>(
    `/research_bar/search`,
    request,
    {
      signal,
      timeout: SEARCH_FLIGHTS_TIMEOUT_MS,
    }
  )
  return response.data
}

export const useSearchFlightsQuery = (request: null | SearchFlightsRequest) =>
  useQuery({
    enabled: request !== null,
    queryFn: async ({ signal }) => (request ? searchFlights(request, signal) : null),
    queryKey: [...flightSearchQueryKeys.search, request],
    retry: false,
    staleTime: BOOKING_SESSION_STALE_TIME,
  })

const searchMultiFlights = async (params: StructuredMultiSearchRequest, signal: AbortSignal) => {
  const response = await apiClient.client.post<NlpSearchFlightsResponse>(
    '/chat/flight/search-multi',
    params,
    { signal, timeout: SEARCH_FLIGHTS_TIMEOUT_MS }
  )
  return response.data
}

export const useMultiFlightSearch = (params: null | StructuredMultiSearchRequest) =>
  useQuery({
    enabled: params !== null,
    queryFn: async ({ signal }) => (params ? searchMultiFlights(params, signal) : null),
    queryKey: [...flightSearchQueryKeys.multiSearch, params],
    retry: false,
    staleTime: BOOKING_SESSION_STALE_TIME,
  })

export const useFetchAffidiumBookingMutation = () =>
  useMutation({
    mutationFn: async (params: { accountCode?: string; bookingReference: string }) => {
      const response = await apiClient.client.get<ExtractedBookingData>(
        `/research_bar/bookings/${params.bookingReference}`,
        {
          params: params.accountCode ? { accountCode: params.accountCode } : undefined,
        }
      )
      return response.data
    },
  })

export const usePriceOfferQuery = (
  request: null | PriceOfferRequest,
  options?: { enabled?: boolean }
) =>
  useQuery({
    enabled: options?.enabled ?? !!request,
    queryFn: async () => {
      if (!request) throw new Error('No request provided')
      const response = await apiClient.client.get<PriceOfferResponse>('/research_bar/price', {
        params: request,
      })
      return response.data
    },
    queryKey: [
      ...flightSearchQueryKeys.priceOffer,
      request?.transactionKey,
      request?.offerKey,
      request?.journeyKeyArray,
    ],
    staleTime: BOOKING_SESSION_STALE_TIME,
  })
